import { React, useState, useEffect, useRef} from 'react'
import { useLocation, useOutletContext, useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { Stack, Row, Col, Alert, Card } from 'react-bootstrap';
import Breadcrumbs from '../Components/Breadcrumbs';
import moment from 'moment';
import 'moment/locale/nl';
import {ReactComponent as PlayButton} from '../svg/play-button-triangle.svg'

import ShakaPlayer from 'shaka-player-react';
import 'shaka-player/dist/controls.css';
import '../shaka-player-style.css';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage, db, analytics } from '../utils/firebase';
import { collection, onSnapshot, addDoc, doc, setDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';
import { v4 as uuidv4 } from 'uuid';
import AcademyLoading from '../Components/AcademyLoading';

export default function Video(props) {
    const {routes, user, series, setSeries, loading, seriesLoading} = useOutletContext();
    const video_id = new URLSearchParams(useLocation().search).get('vid');
    const navigate = useNavigate();
    const [selectedSeries, setSelectedSeries] = useState(null)
    const [video, setVideo] = useState(null)

    // Custom styles
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .shaka-text-container {
                padding-bottom: 20px;
            }
            .shaka-text-container span {
                padding: 0 10px;
            }
            `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Update selectedSeries and video when series data becomes available
    useEffect(() => {
        if (series && series.length > 0 && video_id) {
            const foundSeries = series.find((s) => s.modules && s.modules.some((m) => m.uuid === video_id));
            const foundVideo = foundSeries?.modules.find((m) => m.uuid === video_id);
            setSelectedSeries(foundSeries);
            setVideo(foundVideo);

            // Track video page view
            if (foundSeries && foundVideo) {
                logEvent(analytics, 'page_view', {
                    page_title: `Ukie's Academy - ${foundSeries.name} - ${foundVideo.name}`,
                    page_location: window.location.href,
                    content_type: 'video',
                    series_id: foundSeries.uuid,
                    series_name: foundSeries.name,
                    video_id: foundVideo.uuid,
                    video_name: foundVideo.name,
                    module_number: foundVideo.module,
                    video_length: foundVideo.length
                });
            }
        }
    }, [series, video_id]);

    // Check access using the same logic as Series.js
    const access = selectedSeries ? 
        (selectedSeries.isFree && selectedSeries.freeRequiresSignup && user && user.optedIn) || 
        (selectedSeries.isFree && !selectedSeries.freeRequiresSignup) || 
        (!selectedSeries.isFree && selectedSeries.access)
        : false;

    // Redirect to series page if user doesn't have access (only after data is fully loaded)
    useEffect(() => {
        // Only check access after both series and user data are fully loaded
        if (!loading && !seriesLoading && selectedSeries && video) {
            // Track access check
            logEvent(analytics, 'video_access_check', {
                series_id: selectedSeries.uuid,
                series_name: selectedSeries.name,
                video_id: video.uuid,
                video_name: video.name,
                has_access: access,
                is_free: selectedSeries.isFree,
                requires_signup: selectedSeries.freeRequiresSignup
            });

            if (!access) {
                // Track access denied
                logEvent(analytics, 'video_access_denied', {
                    series_id: selectedSeries.uuid,
                    series_name: selectedSeries.name,
                    video_id: video.uuid,
                    video_name: video.name,
                    reason: selectedSeries.isFree ? 'signup_required' : 'purchase_required'
                });
                
            navigate(`/series?sid=${selectedSeries.uuid}`);
            }
        }
    }, [selectedSeries, video, access, navigate, loading, seriesLoading]);
    const controllerRef = useRef(null);
    const [videoUrl, setVideoUrl] = useState('');
    const [comments, setComments] = useState([]);
    const [commentText, setCommentText] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);
    const [commentCooldown, setCommentCooldown] = useState(false);
    const [cooldownTime, setCooldownTime] = useState(0);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('success');

    // Helper function to format timestamps (handles both Firestore Timestamp and ISO string)
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';
        const momentObj = timestamp.seconds ? moment.unix(timestamp.seconds) : moment(timestamp);
        return momentObj.fromNow();
    };

    useEffect(() => {
        if (!video?.videoLink) return;
        
        // Track video loading start
        logEvent(analytics, 'video_load_start', {
            series_id: selectedSeries?.uuid,
            series_name: selectedSeries?.name,
            video_id: video.uuid,
            video_name: video.name,
            video_path: video.videoLink
        });
        
        // const storageRef = ref(storage, '/videos/cursus-gezond-grazen-module-2/cursus_gezond_grazen_m2_master.m3u8');
        const storageRef = ref(storage, video.videoLink);
        getDownloadURL(storageRef)
            .then((url) => {
                setVideoUrl(url);
                
                // Track successful video load
                logEvent(analytics, 'video_load_success', {
                    series_id: selectedSeries?.uuid,
                    series_name: selectedSeries?.name,
                    video_id: video.uuid,
                    video_name: video.name
                });
            })
            .catch((error) => {
                console.error('Error fetching video:', error);
                
                // Track video load error
                logEvent(analytics, 'video_load_error', {
                    series_id: selectedSeries?.uuid,
                    series_name: selectedSeries?.name,
                    video_id: video.uuid,
                    video_name: video.name,
                    error_message: error.message
                });
            });

        const configureUI = () => {
            if (controllerRef.current?.ui) {
                controllerRef.current.ui.configure({
                    controlPanelElements: [
                        'play_pause',
                        'mute',
                        'volume',
                        'time_and_duration',
                        'spacer',
                        'quality',
                        'cast',
                        'fullscreen',
                        'overflow_menu'
                    ],
                    overflowMenuButtons: ['quality', 'captions', 'playback_rate', 'airplay', 'cast', 'picture_in_picture']
                });
            }
        };

        if (controllerRef.current) {
            const { ui } = controllerRef.current;
            // Wait for UI to be ready
            if (ui) {
                configureUI();
            } else {
                setTimeout(() => configureUI(), 500); // Small delay to allow UI initialization
            }
        }
    }, [video]);

    // Set up live comments listener for the current video's module
    useEffect(() => {
        if (!selectedSeries || !selectedSeries.uuid || !video || !video.uuid) return;

        const commentsRef = collection(db, 'series', selectedSeries.uuid, 'modules', video.uuid, 'comments');
        const unsubscribe = onSnapshot(commentsRef, (snapshot) => {
            const commentsData = snapshot.docs.map(doc => doc.data())
                .filter(comment => !comment.deleted); // Filter out deleted comments
            // Sort comments by pinned first (true before false), then by timestamp (newest first)
            commentsData.sort((a, b) => {
                // First sort by pinned status (pinned comments first)
                if (a.pinned && !b.pinned) return -1;
                if (!a.pinned && b.pinned) return 1;
                
                // Then sort by timestamp (newest first)
                const timestampA = a.timestamp?.seconds || 0;
                const timestampB = b.timestamp?.seconds || 0;
                return timestampB - timestampA;
            });
            setComments(commentsData);
        });

        return () => unsubscribe();
    }, [selectedSeries, video]);

    // Update page title when series and video are loaded
    useEffect(() => {
        if (selectedSeries && selectedSeries.name && video && video.name) {
            document.title = "Ukie's Academy - " + selectedSeries.name + " - " + video.name;
        }
        
        // Cleanup function to reset title when component unmounts
        return () => {
            document.title = "Ukie's Academy";
        };
    }, [selectedSeries, video]);

    // Storage helper functions for incognito compatibility
    const getCooldownFromStorage = (key) => {
        // Try sessionStorage first (works in incognito), then localStorage
        return sessionStorage.getItem(key) || localStorage.getItem(key);
    };

    const setCooldownInStorage = (key, value) => {
        // Always use sessionStorage (works in incognito)
        sessionStorage.setItem(key, value);
        
        // Try localStorage as backup (for non-incognito persistence)
        try {
            localStorage.setItem(key, value);
        } catch (e) {
            // localStorage might not be available in incognito
        }
    };

    // Check for existing cooldown on component mount
    useEffect(() => {
        if (!user?.customer_id) return;
        
        const cooldownKey = `comment_cooldown_${user.customer_id}_${video?.uuid}`;
        const lastCommentTime = getCooldownFromStorage(cooldownKey);
        
        if (lastCommentTime) {
            const timeDiff = Date.now() - parseInt(lastCommentTime);
            const cooldownMs = 2 * 60 * 1000; // 2 minutes in milliseconds
            
            if (timeDiff < cooldownMs) {
                setCommentCooldown(true);
                setCooldownTime(Math.ceil((cooldownMs - timeDiff) / 1000));
                
                // Start countdown
                const interval = setInterval(() => {
                    setCooldownTime(prev => {
                        if (prev <= 1) {
                            setCommentCooldown(false);
                            clearInterval(interval);
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
                
                return () => clearInterval(interval);
            }
        }
    }, [user, video]);

    // Handle comment deletion
    const handleCommentDelete = async (commentUuid) => {
        if (!user?.customer_id || !selectedSeries?.uuid || !video?.uuid) {
            return;
        }

        // Show confirmation dialog
        const confirmed = window.confirm('Weet je zeker dat je deze reactie wilt verwijderen?');
        if (!confirmed) {
            return;
        }

        try {
            // Track comment deletion attempt
            logEvent(analytics, 'comment_delete_attempt', {
                series_id: selectedSeries.uuid,
                series_name: selectedSeries.name,
                video_id: video.uuid,
                video_name: video.name,
                comment_id: commentUuid
            });

            // Update the comment document to mark it as deleted
            const commentDocRef = doc(db, 'series', selectedSeries.uuid, 'modules', video.uuid, 'comments', commentUuid);
            await updateDoc(commentDocRef, {
                deleted: true,
                deletedAt: serverTimestamp(),
                deletedBy: user.customer_id
            });

            // Track successful comment deletion
            logEvent(analytics, 'comment_deleted', {
                series_id: selectedSeries.uuid,
                series_name: selectedSeries.name,
                video_id: video.uuid,
                video_name: video.name,
                comment_id: commentUuid
            });

            // Show success message
            setAlertMessage('Je reactie is succesvol verwijderd.');
            setAlertType('success');
            setShowAlert(true);

        } catch (error) {
            console.error('Error deleting comment:', error);
            
            // Track comment deletion error
            logEvent(analytics, 'comment_delete_error', {
                series_id: selectedSeries?.uuid,
                video_id: video?.uuid,
                comment_id: commentUuid,
                error_message: error.message
            });

            setAlertMessage('Er ging iets mis bij het verwijderen van je reactie. Probeer het opnieuw.');
            setAlertType('danger');
            setShowAlert(true);
        }
    };

    // Handle comment submission
    const handleCommentSubmit = async (e) => {
        e.preventDefault();
        
        // Track comment attempt
        logEvent(analytics, 'comment_attempt', {
            series_id: selectedSeries?.uuid,
            series_name: selectedSeries?.name,
            video_id: video?.uuid,
            video_name: video?.name,
            user_logged_in: !!user?.customer_id,
            has_content: !!commentText.trim(),
            in_cooldown: commentCooldown
        });
        
        if (!user?.customer_id) {
            logEvent(analytics, 'comment_error', {
                series_id: selectedSeries?.uuid,
                video_id: video?.uuid,
                error_type: 'not_logged_in'
            });
            
            setAlertMessage('Je moet ingelogd zijn om een reactie te plaatsen.');
            setAlertType('warning');
            setShowAlert(true);
            return;
        }
        
        if (!commentText.trim()) {
            logEvent(analytics, 'comment_error', {
                series_id: selectedSeries?.uuid,
                video_id: video?.uuid,
                error_type: 'empty_content'
            });
            
            setAlertMessage('Voer een reactie in voordat je deze verstuurt.');
            setAlertType('warning');
            setShowAlert(true);
            return;
        }
        
        if (commentCooldown) {
            logEvent(analytics, 'comment_error', {
                series_id: selectedSeries?.uuid,
                video_id: video?.uuid,
                error_type: 'cooldown_active',
                cooldown_remaining: cooldownTime
            });
            
            setAlertMessage(`Je kunt pas over ${Math.floor(cooldownTime / 60)}:${(cooldownTime % 60).toString().padStart(2, '0')} minuten weer een reactie plaatsen.`);
            setAlertType('warning');
            setShowAlert(true);
            return;
        }
        
        // Validate required data
        if (!selectedSeries?.uuid || !video?.uuid) {
            console.error('Missing required data:', { selectedSeries: selectedSeries?.uuid, video: video?.uuid });
            setAlertMessage('Er is een probleem met het laden van de video data. Probeer de pagina te vernieuwen.');
            setAlertType('danger');
            setShowAlert(true);
            return;
        }
        
        setSubmittingComment(true);
        
        try {
            const commentUuid = uuidv4();
            const commentData = {
                uuid: commentUuid,
                comment: commentText.trim(),
                user: user.fullname || 'Anoniem',
                customer_id: user.customer_id,
                timestamp: serverTimestamp(),
                pinned: false
            };
            
            // console.log('Saving comment with data:', {
            //     seriesId: selectedSeries.uuid,
            //     moduleId: video.uuid,
            //     commentUuid: commentUuid
            // });
            
            // Save comment to Firestore
            const commentDocRef = doc(db, 'series', selectedSeries.uuid, 'modules', video.uuid, 'comments', commentUuid);
            await setDoc(commentDocRef, commentData);
            
            // Track successful comment submission
            logEvent(analytics, 'comment_posted', {
                series_id: selectedSeries.uuid,
                series_name: selectedSeries.name,
                video_id: video.uuid,
                video_name: video.name,
                comment_length: commentText.trim().length,
                user_name: user.fullname || 'Anonymous'
            });
            
            // Set cooldown
            const cooldownKey = `comment_cooldown_${user.customer_id}_${video.uuid}`;
            setCooldownInStorage(cooldownKey, Date.now().toString());
            setCommentCooldown(true);
            setCooldownTime(120); // 2 minutes
            
            // Start countdown
            const interval = setInterval(() => {
                setCooldownTime(prev => {
                    if (prev <= 1) {
                        setCommentCooldown(false);
                        clearInterval(interval);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            
            // Clear form
            setCommentText('');
            
            // Show success message
            setAlertMessage('Je reactie is succesvol geplaatst!');
            setAlertType('success');
            setShowAlert(true);
            
        } catch (error) {
            console.error('Error adding comment:', error);
            
            // Track comment error
            logEvent(analytics, 'comment_error', {
                series_id: selectedSeries?.uuid,
                video_id: video?.uuid,
                error_type: 'technical_error',
                error_message: error.message
            });
            
            setAlertMessage('Er ging iets mis bij het plaatsen van je reactie. Probeer het opnieuw.');
            setAlertType('danger');
            setShowAlert(true);
        } finally {
            setSubmittingComment(false);
        }
    };

    // Track module navigation
    const handleModuleNavigation = (targetVideo) => {
        logEvent(analytics, 'module_navigation', {
            series_id: selectedSeries?.uuid,
            series_name: selectedSeries?.name,
            from_video_id: video?.uuid,
            from_video_name: video?.name,
            to_video_id: targetVideo.uuid,
            to_video_name: targetVideo.name,
            from_module: video?.module,
            to_module: targetVideo.module
        });
    };

    // Auto-hide alert after 5 seconds
    useEffect(() => {
        if (showAlert) {
            const timer = setTimeout(() => {
                setShowAlert(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [showAlert]);

    // Show loading while data is being fetched or series is not found yet
    if(loading || seriesLoading || !selectedSeries) {
        return(
            <AcademyLoading type='Module' />
        ) 
    }
    return (
        <Stack>
            {showAlert && (
                <Alert 
                    variant={alertType} 
                    dismissible 
                    onClose={() => setShowAlert(false)} 
                    style={{
                        position: 'fixed',
                        top: '15px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1050,
                        minWidth: '300px',
                        maxWidth: '600px'
                    }}
                >
                    {alertMessage}
                </Alert>
            )}
            <div className="structure-fullwidth" style={{backgroundImage:'url('+(selectedSeries?.image || '')+')',backgroundPosition:`${selectedSeries?.imagePositionX || 50}% ${selectedSeries?.imagePositionY || 50}%`}}>
                <div className="structure header-banner structure-1 lg:structure-2 items-end" style={{alignItems:'flex-end',height:'500px'}}>
                    <div className="component component-text white text-shadow">
                        <div className="preheader-text white">Ukie's Academy</div>
                        <div className="header-text">{selectedSeries?.name || 'Loading...'}</div>
                    </div>
                </div>
            </div>
            <Breadcrumbs routes={routes} user={user} series={series}/>

            {
                access
                ?
                    <Row style={{padding:'1rem 0rem'}}>
                        {/* <Col xs={12} lg={9} style={{padding:'0', background:'black',border:'1px solid black',borderRight:'none'}}>
                            <ReactPlayer 
                                url='https://vapor-eu-west-3-ukies-files.s3.eu-west-3.amazonaws.com/uploads/assets/video/cursus-gezond-grazen-module-3/cursus_gezond_grazen_m1_master.m3u8'
                                width='100%'
                                height='auto'
                                controls
                                config={{ file: { attributes: { controlsList: 'nodownload' } } }}
                                onContextMenu={e => e.preventDefault()}
                            />
                        </Col> */}
                        <Col xs={12} lg={9} style={{padding:'0', background:'black',border:'1px solid black',borderRight:'none'}}>
                            <ShakaPlayer 
                                ref={controllerRef}
                                // src='https://vapor-eu-west-3-ukies-files.s3.eu-west-3.amazonaws.com/uploads/assets/video/cursus-gezond-grazen-module-3/cursus_gezond_grazen_m1_master.m3u8'
                                // src='https://firebasestorage.googleapis.com/v0/b/ukies-webinars.firebasestorage.app/o/cursus-gezond-grazen-module-2%2Fcursus_gezond_grazen_m2_master.m3u8?alt=media&token=f63caa2c-abb4-4e1b-a389-da0b17907453'
                                src={videoUrl}
                                className='youtube-theme'
                                autoPlay
                            />
                        </Col>
                        <Col xs={12} lg={3} className='video-panel' style={{padding:'0',borderLeft:'4px solid var(--ukies-dark-green)'}}>
                            <Row style={{padding:'0.5rem 0'}}>
                                <span style={{fontSize:'1rem',fontWeight:'bold'}}>Alle modules:</span>
                            </Row>
                            <Row>
                                {
                                    selectedSeries?.modules?.map((vid, idx) => {
                                        return (
                                            <Link 
                                                to={'/series/video?vid=' + vid.uuid}   
                                                style={{margin:0,padding:0}}
                                                key={vid.uuid}
                                                onClick={() => handleModuleNavigation(vid)}
                                            >
                                                <Col xs={12} className={'video-panel-col' + (vid.uuid === video_id ? ' active' : '')}>
                                                    <Card className='video-panel-card'>
                                                        <Card.Title className='module-indicator' style={{padding:0,margin:0,fontSize:'0.75rem'}}>
                                                            
                                                            { vid.uuid === video_id 
                                                                ? <PlayButton style={{width:'0.6rem',height:'auto',fill:'#ffffff'}}/>
                                                                : <span>{vid.module}</span>
                                                            }
                                                        </Card.Title>
                                                        <Card.Img className='thumbnail' variant="left" src={vid.thumbnail} />
                                                        <Card.Body>
                                                            <Card.Title>{vid.name}</Card.Title>
                                                            <Card.Text>{moment.utc(vid.length*1000).format('H') > 0 && moment.utc(vid.length*1000).format('H') + 'u'} {moment.utc(vid.length*1000).format('m')}m {moment.utc(vid.length*1000).format('s')}s</Card.Text>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            </Link>

                                        )
                                    }) || []
                                }
                            </Row>

                        </Col>
                    </Row>
                :
                    <Row style={{padding:'1rem',marginBottom:'-2rem'}}>
                        <a href='https://www.ukies.nl/account/login' target='_parent' style={{padding:'0'}}>
                            <Alert variant='warning'>Je bent niet ingelogd, <b>log in</b> om toegang te krijgen tot jouw cursussen</Alert>     
                        </a>
                    </Row>
            }
            <Row style={{padding:'2rem 0 0 0'}}>
                <div className='component component-text'>
                    <div className='preheader-text-small'>
                        {selectedSeries?.name || 'Loading...'}
                    </div>
                    <div className='header-text-small'>
                        {video?.name || 'Loading...'}
                    </div>

                </div>
            </Row>
            <Row style={{padding:'0 0 2rem 0'}}>
                <div className='component component-text'>
                    <div className='body-text' dangerouslySetInnerHTML={{__html:video?.desciption || ''}}>
                    </div>
                </div>
            </Row>
            { access && selectedSeries && video &&
                <>
                    <Row style={{padding:'2rem 0 0 0'}}>
                        <div className='component component-text'>
                            <div className='header-text-small' style={{padding:0}}>
                                Reacties
                            </div>
                            <div style={{fontSize:'0.8rem'}}>
                                <i>
                                    Stel hier eventuele vragen die je nog hebt. Houd er rekening mee dat het even kan duren voordat ik reageer.
                                </i>
                            </div>
                        </div>
                    </Row>
                    <Row style={{padding:'1rem 0rem',fontSize:'1rem'}}>
                        <form onSubmit={handleCommentSubmit}>
                            <div className="d-flex gap-2">
                                <input 
                                    className='eui-input-wrapper' 
                                    name='comment' 
                                    type='text' 
                                    placeholder='Schrijf een reactie...' 
                                    value={commentText}
                                    onChange={(e) => setCommentText(e.target.value)}
                                    disabled={submittingComment || !user?.customer_id}
                                    style={{ flex: 1 }}
                                />
                                <button 
                                    type='submit' 
                                    disabled={submittingComment || !commentText.trim() || commentCooldown || !user?.customer_id}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: commentCooldown ? '#6c757d' : '#3b4f46',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: (submittingComment || !commentText.trim() || commentCooldown || !user?.customer_id) ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {submittingComment ? 'Versturen...' : 
                                     commentCooldown ? `${Math.floor(cooldownTime / 60)}:${(cooldownTime % 60).toString().padStart(2, '0')}` : 
                                     'Versturen'}
                                </button>
                            </div>
                            {!user?.customer_id && (
                                <small style={{color: '#6c757d', fontSize: '0.75rem', marginTop: '0.5rem', display: 'block'}}>
                                    Je moet ingelogd zijn om een reactie te plaatsen.
                                </small>
                            )}
                        </form>
                    </Row>
                    <Row style={{gap:'1.5rem', padding:'1rem 0rem'}} className='comments-container'>
                        {comments.length > 0 ? (
                            comments.map((comment, index) => (
                                <Card key={comment.uuid || index} className='academy-comment-card' xs={12}>
                                    <Card.Header>
                                        {comment.pinned && (
                                            <Card.Text className="pinned">
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                                                    <path d="M16 11V3h1V2H7v1h1v8l-2 2v2h5v6l1 1 1-1v-6h5v-2l-2-2zm1 3H7v-.59l1.71-1.71.29-.29V3h6v8.41l.29.29L17 13.41V14z"></path>
                                                </svg>
                                                <span></span>
                                                Pinned by Marloes
                                            </Card.Text>
                                        )}
                                        <Card.Text as='div' style={{display: 'flex', justifyContent: 'start', alignItems: 'flex-end', gap: '1rem'}}>
                                            <div style={{display:'flex',alignItems:'end'}}>
                                                <b>{comment.user || 'Anoniem'}</b> 
                                                <span style={{fontSize:'0.7rem',color:'gray', paddingLeft:'0.5rem'}}>
                                                    {formatTimestamp(comment.timestamp)}
                                                </span>
                                                {comment.customer_id === user?.customer_id && (
                                                <button
                                                    onClick={() => handleCommentDelete(comment.uuid)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        padding: '0px',
                                                        marginLeft: '1rem',
                                                        color: '#910714',
                                                        fontSize: '0.7rem',
                                                        display: 'flex',
                                                        alignItems: 'baseline',
                                                        gap: '4px'
                                                    }}
                                                    title="Verwijder reactie"
                                                >
                                                    <svg 
                                                        width="12" 
                                                        height="12" 
                                                        viewBox="0 0 24 24" 
                                                        fill="#910714"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path d="M3 6h18v2H3V6zm2 3h14l-1 14H6L5 9zm5-6h4v1H10V3z"/>
                                                    </svg>
                                                    <span>Verwijderen</span>
                                                </button>
                                            )}
                                            </div>

                                        </Card.Text>
                                    </Card.Header>
                                    <Card.Body>
                                        {comment.comment}
                                    </Card.Body>
                                    {comment.response && (
                                        <Card.Footer>
                                            <Card className='academy-comment-card comment-response' xs={12}>
                                                <Card.Header>
                                                    <Card.Text>
                                                        <b>Marloes Vogel</b> 
                                                        <span style={{fontSize:'0.7rem',color:'gray', paddingLeft:'0.5rem'}}>
                                                            {formatTimestamp(comment.response.timestamp)}
                                                        </span>
                                                    </Card.Text>
                                                </Card.Header>
                                                <Card.Body>
                                                    {comment.response.comment}
                                                </Card.Body>
                                            </Card>
                                        </Card.Footer>
                                    )}
                                </Card>
                            ))
                        ) : (
                            <div style={{padding: '2rem', textAlign: 'center', color: '#6c757d'}}>
                                Er zijn nog geen reacties geplaatst
                            </div>
                        )}
                    </Row>
                </>

            }
        </Stack>
    );
}